/**
 * Bulk-import review grid → the revision a reference resolved to, when its
 * calibre matched a revision line (bulk-import revision resolver).
 *
 * The badge says which revision was picked and why ("Picked ETA 2824-2 via
 * brand founded ≈ 2024"). Clicking it opens three overrides for this row only:
 * pick a revision from the line, type a year (re-runs the resolver), or ✨ ask
 * the AI for the reference's release year (one web search, then re-run).
 */
import { useState } from 'react';
import {
  revisionPickReason,
  sortMembers,
  type RevisionConfidence,
  type RevisionResolution,
} from '../../services/calibreLineageLogic';
import type { RevisionLineMember } from './importCalibreStamp';

export interface RevisionPickState {
  /** Every live member of the matched line. */
  line: RevisionLineMember[];
  resolution: RevisionResolution<RevisionLineMember>;
}

interface Props {
  pick: RevisionPickState;
  onPick: (memberId: string) => void;
  onYear: (year: number) => void;
  onAi: () => void;
  /** This row's ✨ lookup is running. */
  aiBusy: boolean;
  /** Another row's ✨ lookup is running (one at a time). */
  aiBlocked: boolean;
}

const TONE: Record<RevisionConfidence, string> = {
  high: 'bg-green-50 text-green-800 border-green-200',
  medium: 'bg-amber-50 text-amber-800 border-amber-200',
  low: 'bg-gray-50 text-gray-700 border-gray-200',
};

const years = (m: RevisionLineMember) =>
  typeof m.activeFromYear === 'number' ? ` (${m.activeFromYear}–${m.activeUntilYear ?? ''})` : '';

export function RevisionPickCell({ pick, onPick, onYear, onAi, aiBusy, aiBlocked }: Props) {
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState('');
  const { resolution } = pick;
  const reason = revisionPickReason(resolution);
  const members = sortMembers(pick.line.filter((m) => !m.isLineagePlaceholder));
  const applyYear = () => {
    const y = Number(year.trim());
    if (Number.isInteger(y) && y >= 1750 && y <= 2200) {
      onYear(y);
      setYear('');
    }
  };

  return (
    <div className="flex flex-col gap-1 items-start" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={`${reason} · ${resolution.confidence} confidence. Click to change this row's revision.`}
        aria-expanded={open}
        className={`max-w-[220px] truncate rounded border px-1.5 py-0.5 text-[10px] ${TONE[resolution.confidence]}`}
      >
        {reason}
      </button>
      {open && (
        <div className="flex flex-col gap-1 rounded border border-gray-200 bg-white p-1.5 shadow-sm">
          <select
            aria-label="Revision for this reference"
            value={resolution.member.id}
            onChange={(e) => onPick(e.target.value)}
            className="rounded border-gray-300 text-[11px] py-0.5"
          >
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
                {years(m)}
                {m.isCurrent ? ' · current' : ''}
              </option>
            ))}
          </select>
          <div className="flex items-center gap-1">
            <input
              aria-label="Year for this reference"
              inputMode="numeric"
              placeholder="Year"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') applyYear();
              }}
              className="w-16 rounded border border-gray-300 px-1 py-0.5 text-[11px]"
            />
            <button
              type="button"
              onClick={applyYear}
              className="rounded border border-gray-300 px-1.5 py-0.5 text-[11px] text-gray-700 hover:bg-gray-50"
            >
              Use year
            </button>
            <button
              type="button"
              onClick={onAi}
              disabled={aiBusy || aiBlocked}
              aria-label="Look up the release year with AI"
              title={
                aiBlocked
                  ? 'Another row is looking up its year — one at a time.'
                  : 'Search the web for this reference’s release year, then re-pick the revision.'
              }
              className="rounded border border-gray-300 px-1.5 py-0.5 text-[11px] hover:bg-gray-50 disabled:opacity-50"
            >
              {aiBusy ? '…' : '✨'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
